import { useState } from "react";
import { KeyRound } from "lucide-react";
import {
  useAdminApiKeyImpact,
  useAdminApiKeys,
  useRevokeAdminApiKey,
  useRotateAdminApiKey,
  useSetAdminApiKeyScopes,
  useUnlinkAdminApiKeyOrg,
} from "../../hooks/queries";
import type { AdminApiKeyImpact } from "../../types/contract";
import { Panel } from "./SystemDashboardPanels";
import { ApiKeyConfirm } from "./ApiKeyConfirm";
import { ApiKeySecretModal } from "./ApiKeySecretModal";
import { apiMessage, CreateKeyForm } from "./ApiKeyForm";
import { KeysTable, type KeyConfirm } from "./ApiKeysTable";

function impactSentence(
  loading: boolean,
  data: AdminApiKeyImpact | undefined,
): string {
  if (loading || !data) {
    return "… open work orders, … running jobs. The partner loses visibility of these; nothing is cancelled.";
  }
  const orders = data.open_work_orders;
  const jobs = data.open_jobs;
  return `${orders} open work orders, ${jobs} running jobs. The partner loses visibility of these; nothing is cancelled.`;
}

function confirmCopy(
  confirm: KeyConfirm,
  loading: boolean,
  data: AdminApiKeyImpact | undefined,
): { title: string; label: string; lines: string[] } {
  if (confirm.kind === "unlink") {
    const lines = [impactSentence(loading, data)];
    if (confirm.key.orgs.length <= 1) {
      lines.push(
        "This key will have no organizations and can't read any field.",
      );
    }
    return {
      title: "Unlink organization?",
      label: "Unlink organization",
      lines,
    };
  }
  if (confirm.kind === "revoke") {
    return {
      title: "Revoke key?",
      label: "Revoke key",
      lines: [
        impactSentence(loading, data),
        "Revocation takes effect within about 30 seconds and can't be undone.",
      ],
    };
  }
  if (confirm.kind === "rotate") {
    return {
      title: "Rotate key?",
      label: "Rotate key",
      lines: [
        "Creates a new key with the same scopes, orgs and cap. The old key keeps working until you revoke it.",
      ],
    };
  }
  return {
    title: "Remove every scope?",
    label: "Remove scopes",
    lines: ["This key will only be able to list organizations and jobs."],
  };
}

function runConfirm(
  confirm: KeyConfirm,
  actions: {
    unlink: ReturnType<typeof useUnlinkAdminApiKeyOrg>;
    revoke: ReturnType<typeof useRevokeAdminApiKey>;
    rotate: ReturnType<typeof useRotateAdminApiKey>;
    setScopes: ReturnType<typeof useSetAdminApiKeyScopes>;
  },
  onClose: () => void,
  onIssued: (token: string, prefix: string) => void,
  setError: (message: string) => void,
) {
  const done = {
    onSuccess: () => onClose(),
    onError: (err: unknown) => setError(apiMessage(err)),
  };
  if (confirm.kind === "unlink") {
    actions.unlink.mutate(
      { keyId: confirm.key.key_id, orgId: confirm.orgId },
      done,
    );
    return;
  }
  if (confirm.kind === "revoke") {
    actions.revoke.mutate(confirm.key.key_id, done);
    return;
  }
  if (confirm.kind === "rotate") {
    actions.rotate.mutate(confirm.key.key_id, {
      onSuccess: (issued) => {
        onIssued(issued.token, issued.prefix);
        actions.rotate.reset();
        onClose();
      },
      onError: (err) => setError(apiMessage(err)),
    });
    return;
  }
  actions.setScopes.mutate(
    { keyId: confirm.key.key_id, scopes: confirm.scopes },
    done,
  );
}

function ConfirmDialog({
  confirm,
  onClose,
  onIssued,
}: {
  confirm: KeyConfirm;
  onClose: () => void;
  onIssued: (token: string, prefix: string) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const impactKey =
    confirm.kind === "unlink" || confirm.kind === "revoke"
      ? confirm.key.key_id
      : null;
  const impactOrg = confirm.kind === "unlink" ? confirm.orgId : null;
  const impact = useAdminApiKeyImpact(impactKey, impactOrg, impactKey != null);
  const unlink = useUnlinkAdminApiKeyOrg();
  const revoke = useRevokeAdminApiKey();
  const rotate = useRotateAdminApiKey();
  const setScopes = useSetAdminApiKeyScopes();
  const busy =
    unlink.isPending ||
    revoke.isPending ||
    rotate.isPending ||
    setScopes.isPending;
  const copy = confirmCopy(
    confirm,
    impact.isLoading || impact.isFetching,
    impact.data,
  );
  return (
    <ApiKeyConfirm
      title={copy.title}
      confirmLabel={copy.label}
      busy={busy}
      onClose={onClose}
      onConfirm={() => {
        setError(null);
        runConfirm(
          confirm,
          { unlink, revoke, rotate, setScopes },
          onClose,
          onIssued,
          setError,
        );
      }}
      body={
        <>
          {copy.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {error && <p className="text-red-400">{error}</p>}
        </>
      }
    />
  );
}

export function ApiKeysPanel({ enabled }: { enabled: boolean }) {
  const keys = useAdminApiKeys(enabled);
  const [secret, setSecret] = useState<{
    token: string;
    prefix: string;
  } | null>(null);
  const [confirm, setConfirm] = useState<KeyConfirm | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = Array.isArray(keys.data) ? keys.data : [];
  return (
    <>
      <Panel
        title="API keys"
        icon={<KeyRound size={12} />}
        state={{ isLoading: keys.isLoading, isError: keys.isError }}
      >
        <div className="mb-2">
          <button
            type="button"
            className="rounded border border-line px-2 py-1 text-[11px] hover:bg-hover"
            onClick={() => setCreating(true)}
          >
            New key
          </button>
        </div>
        {creating && (
          <CreateKeyForm
            enabled={enabled}
            onClose={() => setCreating(false)}
            onIssued={(token, prefix) => {
              setSecret({ token, prefix });
              setCreating(false);
            }}
          />
        )}
        <KeysTable rows={rows} enabled={enabled} onConfirm={setConfirm} />
        <p className="mt-2 text-[10px] text-faint">
          Changes apply within about 30 seconds.
        </p>
      </Panel>
      {secret && (
        <ApiKeySecretModal
          token={secret.token}
          prefix={secret.prefix}
          onClose={() => setSecret(null)}
        />
      )}
      {confirm && (
        <ConfirmDialog
          confirm={confirm}
          onClose={() => setConfirm(null)}
          onIssued={(token, prefix) => setSecret({ token, prefix })}
        />
      )}
    </>
  );
}
